import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Row,
  Spin,
  Form,
  Button,
  Tooltip,
  message,
} from 'antd';
import Table from 'components/Table';
import { getTramites } from 'api/tramites/tramites';
import { logger } from 'utils/debug';
import { routePermission } from 'utils/permission';
import { permissionConfig as gpmPermissionConfig } from 'views/Tramites/GPM';
import ModalComparacionTramite from './ModalComparacionTramite';

export const permissionConfig = {
  model: 'tramite',
};

const URL_TRAMITE = '/tramites/registrar-tramite';

// eslint-disable-next-line react/prop-types
const HistorialDeTramites = ({ predio, permission }) => {
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [tramiteSeleccionado, setTramiteSeleccionado] = useState(null);

  useEffect(() => {
    let mounted = true;

    const parseJSONFields = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;

      const jsonFields = [
        'comentarios_del_tramite',
        'inf_final_padron',
        'inf_inicial_solicitante',
        'inf_final_solicitante',
        'inf_inicial_padron',
      ];

      const result = { ...obj };

      jsonFields.forEach((field) => {
        if (result[field] && typeof result[field] === 'string') {
          try {
            result[field] = JSON.parse(result[field]);
          } catch (error) {
            logger(`Error parsing ${field}:`, error);
          }
        }
      });

      Object.keys(result).forEach((key) => {
        if (Array.isArray(result[key])) {
          result[key] = result[key].map((item) => parseJSONFields(item));
        } else if (typeof result[key] === 'object' && result[key] !== null) {
          result[key] = parseJSONFields(result[key]);
        }
      });

      return result;
    };

    const fetchData = async () => {
      setLoading(true);
      try {
        const _data = await getTramites({ object_id_padron: predio, tipo_de_padron: 3 });
        if (mounted) {
          const processedData = _data.map((tramite) => parseJSONFields(tramite));
          setData(processedData);
        }
      } catch (error) {
        if (mounted) {
          setData([]);
          message.error('Error al cargar el historial de trámites');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (predio) {
      fetchData();
    } else {
      setData([]);
    }

    return () => {
      mounted = false;
    };
  }, [predio]);

  const puedeAbrirTramite = routePermission(URL_TRAMITE, gpmPermissionConfig);

  const abrirTramite = (record) => {
    window.open(`${URL_TRAMITE}/?tramite=${record.id}`, '_blank', 'noopener,noreferrer');
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      width: 320,
      render: (text, record) => (
        <>
          <Button
            type="link"
            className="primary-text no-color"
            onClick={(e) => {
              e.preventDefault();
              setTramiteSeleccionado(record);
              setModalVisible(true);
            }}
            disabled={loading}
          >
            {text}
          </Button>
          <Tooltip title={puedeAbrirTramite ? '' : 'Sin permisos para abrir el trámite'}>
            <Button
              type="link"
              onClick={(e) => {
                e.stopPropagation();
                abrirTramite(record);
              }}
              disabled={loading || !puedeAbrirTramite}
            >
              Abrir trámite en seguimiento
            </Button>
          </Tooltip>
        </>
      ),
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
    {
      titleText: 'Fecha de Inicio',
      dataIndex: 'fecha_de_inicio',
      key: 'fecha_de_inicio',
      width: 150,
      render: (val) => val && new Date(val).toLocaleDateString(),
    },
    {
      titleText: 'Fecha de Finalización',
      dataIndex: 'fecha_de_finalizacion',
      key: 'fecha_de_finalizacion',
      width: 150,
      render: (val) => val && new Date(val).toLocaleDateString(),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (e) => estadosGlobales.find((i) => i.id === e)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          mobileColIndex={0}
          permission={permission}
          rowKey="id"
          noControls
          allowSort={false}
        />
        {tramiteSeleccionado && (
          <ModalComparacionTramite
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
            titulo="Comparación de Información del Padrón"
            inicial={tramiteSeleccionado.inf_inicial_padron}
            final={tramiteSeleccionado.inf_final_padron}
          />
        )}
      </Spin>
    </Row>
  );
};

HistorialDeTramites.propTypes = {
  predio: PropTypes.number,
};

HistorialDeTramites.defaultProps = {
  predio: null,
};

export default HistorialDeTramites;
